import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import api from "../../api/client";
import { useAuth } from "../Data/AuthContext";

function Check() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

export default function Sign_IN() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  // Carried over from registration so the "check your email" message lands
  // on the screen the user is redirected to.
  const notice = location.state?.notice;

  const [email, setEmail] = useState(location.state?.email || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotMsg, setForgotMsg] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");

    if (!email.trim() || !password) {
      setError("Enter both your email address and password.");
      return;
    }

    setLoading(true);
    try {
      const response = await api.post("/users/login", { email, password });

      // The login response already carries role and name, so no follow-up
      // profile request is needed.
      const profile = login(response.data);

      // Honour wherever the route guard intercepted the user on the way in.
      const redirectTo =
        location.state?.from?.pathname ||
        location.state?.from ||
        (profile.role === "admin" ? "/AdminDash" : "/");
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "We couldn't sign you in. Check your details and try again."
      );
      setPassword("");
    } finally {
      setLoading(false);
    }
  }

  async function handleForgot(e) {
    e.preventDefault();
    setForgotMsg(null);

    if (!forgotEmail.trim()) {
      setForgotMsg({ kind: "error", text: "Enter the email address for your account." });
      return;
    }

    setForgotBusy(true);
    try {
      const res = await api.post("/users/forget-password", { email: forgotEmail.trim() });
      setForgotMsg({
        kind: "success",
        text:
          res.data?.message ||
          "If that address has an account, a reset link is on its way.",
      });
    } catch (err) {
      setForgotMsg({
        kind: "error",
        text:
          err.response?.data?.message ||
          "Could not send the reset link. Please try again shortly.",
      });
    } finally {
      setForgotBusy(false);
    }
  }

  return (
    <div className="es-auth">
      <aside className="es-auth__aside">
        <div>
          <Link to="/" className="es-brand" style={{ color: "#fff" }}>
            <span className="es-brand__mark" aria-hidden="true">ES</span>
            <span className="es-brand__name">
              <strong>Edu</strong>
              <span style={{ color: "var(--es-sand-400)" }}>Special</span>
            </span>
          </Link>
        </div>

        <div>
          <h2 className="es-auth__aside-title">
            Everything for your family, in one place
          </h2>
          <p>
            Sign in to manage programme registrations, keep your details current
            and review your support at any time.
          </p>

          <ul className="es-auth__points">
            <li><Check /> Track the programmes you&rsquo;re enrolled in</li>
            <li><Check /> Update your profile and contact details</li>
            <li><Check /> See upcoming events before they open publicly</li>
          </ul>
        </div>

        <p style={{ fontSize: "0.85rem", color: "#a9c5c2" }}>
          Step-free access · Low-stimulation waiting room on request
        </p>
      </aside>

      <div className="es-auth__panel">
        <div className="es-auth__form es-rise">
          <Link to="/" className="es-auth__back">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="m15 18-6-6 6-6" />
            </svg>
            Back to site
          </Link>

          <h1 className="es-h1" style={{ fontSize: "1.9rem" }}>Welcome back</h1>
          <p className="es-lede" style={{ marginBottom: "1.75rem", fontSize: "0.98rem" }}>
            Sign in to your EduSpecial account.
          </p>

          {notice && !error && (
            <div className="es-alert es-alert--success" role="status">{notice}</div>
          )}

          {error && (
            <div className="es-alert es-alert--error" role="alert">{error}</div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div className="es-field">
              <label className="es-label" htmlFor="signin-email">Email address</label>
              <input
                id="signin-email"
                type="email"
                className="es-input"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div className="es-field">
              <label className="es-label" htmlFor="signin-password">Password</label>
              <input
                id="signin-password"
                type="password"
                className="es-input"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            <div className="es-auth__meta">
              <button
                type="button"
                className="es-linkbtn"
                onClick={() => {
                  setForgotOpen(true);
                  setForgotEmail(email);
                  setForgotMsg(null);
                }}
              >
                Forgot your password?
              </button>
            </div>

            <button
              type="submit"
              className="es-btn es-btn--primary"
              style={{ width: "100%" }}
              disabled={loading}
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <p className="es-auth__switch">
            New to EduSpecial? <Link to="/signup">Create an account</Link>
          </p>
        </div>
      </div>

      {forgotOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="forgot-title"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 60,
            background: "rgb(31 41 51 / 0.45)",
            display: "grid",
            placeItems: "center",
            padding: "1rem",
          }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setForgotOpen(false);
          }}
        >
          <div className="es-card" style={{ maxWidth: "26rem", width: "100%" }}>
            <div className="es-card__body">
              <h2 className="es-h2" id="forgot-title">Reset your password</h2>
              <p className="es-body" style={{ fontSize: "0.93rem" }}>
                Enter your email address and we&rsquo;ll send a link to set a new
                password.
              </p>

              {forgotMsg && (
                <div
                  className={`es-alert es-alert--${
                    forgotMsg.kind === "success" ? "success" : "error"
                  }`}
                  role={forgotMsg.kind === "success" ? "status" : "alert"}
                >
                  {forgotMsg.text}
                </div>
              )}

              <form onSubmit={handleForgot} noValidate>
                <div className="es-field">
                  <label className="es-label" htmlFor="forgot-email">Email address</label>
                  <input
                    id="forgot-email"
                    type="email"
                    className="es-input"
                    autoComplete="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                  />
                </div>

                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <button type="submit" className="es-btn es-btn--primary" disabled={forgotBusy}>
                    {forgotBusy ? "Sending…" : "Send reset link"}
                  </button>
                  <button
                    type="button"
                    className="es-btn es-btn--ghost"
                    onClick={() => setForgotOpen(false)}
                  >
                    Close
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
